import { useCallback, useEffect, useState } from 'react';

import { deleteFileFromOSS } from '../api/attachments';
import {
  isFileRejected,
  mapValidationErrorToTranslationKey,
} from '../helpers/isFileRejected';
import { useTranslation } from '../i18n';
import { AttachmentDraft, UploadError } from '../types';

type UseDraftsReturnValue = {
  drafts: AttachmentDraft[];
  addDrafts: (drafts: AttachmentDraft[]) => void;
  deleteDraft: (draftId: string) => void;
  updateDraft: ({
    draftId,
    data,
  }: {
    draftId: string;
    data: Partial<AttachmentDraft>;
  }) => void;
  onDraftUploadFailed: ({
    draftId,
    error,
  }: {
    draftId: string;
    error: unknown;
  }) => void;
  discardUnlinkedDrafts: () => Promise<void>;
  deleteDraftAndFileFromOSS: (draftId: string) => Promise<void>;
};

const useDrafts = (
  onDeleteDraft?: (draftId: string) => void,
): UseDraftsReturnValue => {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState<AttachmentDraft[]>([]);

  useEffect(() => {
    return () => {
      const unlinkedDrafts = drafts.filter((draft) => !draft.storageUrn);

      if (unlinkedDrafts.length) {
        console.warn(
          '[Attachments] There are unlinked drafts. Make sure to call discardUnlinkedDrafts() before unmounting the component.',
        );
      }
    };
    // we want this warning to be called only once, on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addDrafts = useCallback((drafts: AttachmentDraft[]) => {
    setDrafts((prevDrafts) => [...drafts, ...prevDrafts]);
  }, []);

  const updateDraft = useCallback(
    ({
      draftId,
      data,
    }: {
      draftId: string;
      data: Partial<AttachmentDraft>;
    }) => {
      setDrafts((prevDrafts) =>
        prevDrafts.map((draft) => {
          if (draft.attachmentId !== draftId) {
            return draft;
          }

          return {
            ...draft,
            ...data,
          };
        }),
      );
    },
    [],
  );

  const getDraftErrorMessage = useCallback(
    (error: UploadError) => {
      const translationKey =
        mapValidationErrorToTranslationKey[error?.code] || 'GENERAL_ERROR';

      return t(translationKey);
    },
    [t],
  );

  const onDraftUploadFailed = useCallback(
    ({ draftId, error }: { draftId: string; error: unknown | UploadError }) => {
      const rejected = isFileRejected(error);

      updateDraft({
        draftId,
        data: {
          rejected,
          error: getDraftErrorMessage(
            error as UploadError,
          ) as unknown as string,
        },
      });
    },
    [updateDraft, getDraftErrorMessage],
  );

  const deleteDraft = useCallback(
    (draftId: string) => {
      setDrafts((prevDrafts) =>
        prevDrafts.filter((draft) => draft.attachmentId !== draftId),
      );
      if (onDeleteDraft) {
        onDeleteDraft(draftId);
      }
    },
    [onDeleteDraft],
  );

  /**
   * Delete a draft and its file from OSS
   */
  const deleteDraftAndFileFromOSS = useCallback(
    async (draftId: string) => {
      const draftToDelete = drafts.find(
        (draft) => draft.attachmentId === draftId,
      ) as AttachmentDraft;

      if (draftToDelete.storageUrn) {
        await deleteFileFromOSS(draftToDelete.storageUrn);
      }

      deleteDraft(draftId);
    },
    [deleteDraft, drafts],
  );

  /**
   * Discard all unlinked drafts
   */
  const discardUnlinkedDrafts = useCallback(async () => {
    for (const draft of drafts) {
      if (!draft.storageUrn) {
        deleteDraft(draft.attachmentId);
        return;
      }

      await deleteDraftAndFileFromOSS(draft.attachmentId);
    }
  }, [deleteDraftAndFileFromOSS, deleteDraft, drafts]);

  return {
    drafts,
    addDrafts,
    deleteDraft,
    deleteDraftAndFileFromOSS,
    updateDraft,
    onDraftUploadFailed,
    discardUnlinkedDrafts,
  };
};

export default useDrafts;
